import type { CarCatalogPlacementRecord } from '@navio/nsdk';
import { cva, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { NoddiHoverTooltip } from 'noddi-ui-web';

type Props = {
  placement: CarCatalogPlacementRecord | null | undefined;
  /** Registry make name. Shown per level in the tooltip breakdown. */
  make?: string;
  /** Registry model name. Shown per level in the tooltip breakdown. */
  model?: string;
};

type Status = 'modification' | 'generation' | 'unmapped';

const STATUS_ICON: Record<Status, NoddiIconName> = {
  modification: 'CircleCheck',
  generation: 'CircleAlert',
  unmapped: 'CircleX'
};

const STATUS_TITLE: Record<Status, string> = {
  modification: 'Catalog match: exact modification',
  generation: 'Catalog match: generation only',
  unmapped: 'No catalog match'
};

/** One hierarchy level in the tooltip: matched, pending (generation-only) or unmatched, with its value. */
type LevelState = 'matched' | 'pending' | 'unmatched';

const LEVEL_ICON: Record<LevelState, NoddiIconName> = {
  matched: 'CircleCheck',
  pending: 'CircleAlert',
  unmatched: 'CircleX'
};

const levelVariants = cva('shrink-0', {
  variants: {
    state: {
      matched: 'text-successDark',
      pending: 'text-warningDark',
      unmatched: 'text-destructive'
    }
  }
});

export const CarModificationStatusIcon = ({ placement, make, model }: Props) => {
  const modification = placement?.modificationName.trim();
  const status: Status = modification ? 'modification' : placement != null ? 'generation' : 'unmapped';
  const generation = placement?.generationName.trim();
  const years =
    placement?.generationYearFrom != null
      ? ` (${placement.generationYearFrom}–${placement.generationYearTo ?? 'ongoing'})`
      : '';

  const levels = [
    { label: 'Make', value: make, state: placement != null ? ('matched' as const) : ('unmatched' as const) },
    { label: 'Model', value: model, state: placement != null ? ('matched' as const) : ('unmatched' as const) },
    {
      label: 'Generation',
      value: placement != null ? `${generation || 'linked node'}${years}` : undefined,
      state: placement != null ? ('matched' as const) : ('unmatched' as const)
    },
    {
      label: 'Modification',
      value: modification,
      state: modification ? ('matched' as const) : placement != null ? ('pending' as const) : ('unmatched' as const)
    }
  ];

  const label = [
    STATUS_TITLE[status],
    ...levels.map((level) => `${level.label}: ${level.value ?? 'not matched'}`)
  ].join('. ');

  return (
    <NoddiHoverTooltip
      content={
        <div className='flex flex-col gap-2'>
          <span className='font-semibold'>{STATUS_TITLE[status]}</span>
          <div className='flex flex-col gap-1'>
            {levels.map((level) => (
              <div key={level.label} className='flex items-center gap-1.5'>
                <NoddiIcon
                  name={LEVEL_ICON[level.state]}
                  size='xSmall'
                  className={levelVariants({ state: level.state })}
                />
                <span className='text-muted-foreground'>{level.label}</span>
                <span>{level.value ?? 'not matched'}</span>
              </div>
            ))}
          </div>
          {status === 'generation' && (
            <span className='text-muted-foreground'>
              The link stops at the generation, so trim data (engine, torque, pressures) stays tentative.
            </span>
          )}
          {status === 'unmapped' && (
            <span className='text-muted-foreground'>
              The automatic match found no catalog node for this registry identity. Open the car and review Matching
              details.
            </span>
          )}
        </div>
      }
    >
      <span className={statusVariants({ status })} role='img' aria-label={label}>
        <NoddiIcon name={STATUS_ICON[status]} size='small' />
      </span>
    </NoddiHoverTooltip>
  );
};

const statusVariants = cva('inline-flex items-center', {
  variants: {
    status: {
      modification: 'text-successDark',
      generation: 'text-warningDark',
      unmapped: 'text-destructive'
    }
  }
});
